// THE COMMAND LIST — the ONE list of app actions behind the shell's command
// dock (App.jsx `commands=`), the ink/synapse pattern: the dock is the
// ecosystem's COMMAND CENTER — a verb-grammar palette + the session's flight
// recorder (every executed line streams into its transcript). Missions files
// its commands under the shell's canonical headings (DOCK_GROUPS); rows call
// the same window/DOM seams app.js exposes (the legacy engine owns the pages,
// the builder, and the chat).
import { DOCK_GROUPS, PANEL_HOTKEYS, formatHotkey } from '@ofself/shell';

// THE hotkey map — one place; the shell dispatches these (each command's
// `hotkey` field) and renders them in the dock: the commands list IS the
// hotkeys database. The PANEL bindings (PANEL_HOTKEYS, ⌘\ / ⇧⌘\) are declared
// on the two panel rows below — the rail that used to dispatch them is gone
// (shell 0.257.0).
export const MISSIONS_HOTKEYS = {
  newMission: 'mod+alt+n',
};

// Page nav mirrored from App's NAV ids — labels only (the dock renders no
// icons for navigate rows; the page menu owns the glyphs).
const PAGES = [
  { id: 'missions',    label: 'Missions',    fn: 'navToMissions' },
  { id: 'now',         label: 'Now',         fn: 'navToNow' },
  { id: 'schedule',    label: 'Calendar',    fn: 'navToSchedule' },
  { id: 'lookback',    label: 'Lookback',    fn: 'navToLookback' },
  { id: 'manage',      label: 'Manage',      fn: 'navToManage' },
];

/**
 * Build the dock's command list.
 * ctx:
 *   toggleSidebar() — left (page menu) panel toggle
 *   toggleAgent()   — right (agent) panel toggle
 *   openAgent()     — open the agent panel (without toggling)
 */
export function buildMissionsCommands(ctx) {
  return [
    // ── Commands (the default heading) ──────────────────────────────────
    {
      id: 'new-mission',
      label: 'New mission',
      keywords: ['create', 'domain', 'builder', 'add', 'care'],
      hotkey: MISSIONS_HOTKEYS.newMission,
      // The legacy builder — app.js wires it onto the index FAB.
      run: () => document.getElementById('new-mission-fab')?.click(),
    },
    {
      id: 'new-chat',
      label: 'New chat',
      keywords: ['agent', 'reset', 'conversation', 'clear'],
      run: () => {
        ctx.openAgent?.();
        document.getElementById('chat-new')?.click();
      },
    },
    // ── Navigate ────────────────────────────────────────────────────────
    {
      id: 'panel-left',
      label: 'Toggle left panel',
      group: DOCK_GROUPS.NAVIGATE,
      keywords: ['sidebar', 'menu', 'pages', 'collapse'],
      // the shell's rail dispatched this binding until 0.257.0 retired the
      // rail — the command declares it now, like every other hotkey
      hotkey: PANEL_HOTKEYS.left,
      shortcut: formatHotkey(PANEL_HOTKEYS.left),
      run: ctx.toggleSidebar,
    },
    {
      id: 'panel-right',
      label: 'Toggle right panel',
      group: DOCK_GROUPS.NAVIGATE,
      keywords: ['agent', 'help', 'assistant', 'chat'],
      hotkey: PANEL_HOTKEYS.right,
      shortcut: formatHotkey(PANEL_HOTKEYS.right),
      run: ctx.toggleAgent,
    },
    ...PAGES.map((p) => ({
      id: `go-${p.id}`,
      label: `Go to ${p.label}`,
      group: DOCK_GROUPS.NAVIGATE,
      keywords: ['page', 'view', p.label.toLowerCase()],
      run: () => window[p.fn]?.(),
    })),
  ];
}
